import React, { useState } from 'react';
import { 
  BarChart3, TrendingUp, DollarSign, Package, Users, Star,
  Calendar, Download, Filter, ArrowUp, ArrowDown, Eye
} from 'lucide-react';

const SellerAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [analyticsData] = useState({
    revenue: 45600,
    orders: 156,
    customers: 89,
    rating: 4.8
  });

  // Dummy data for top products
  const topProducts = [
    { name: 'Premium Cotton T-Shirt', sales: 245, revenue: '$7,350', trend: 'up' },
    { name: 'Handwoven Jute Bag', sales: 189, revenue: '$3,780', trend: 'up' },
    { name: 'Traditional Jamdani Saree', sales: 156, revenue: '$14,040', trend: 'down' },
    { name: 'Leather Wallet', sales: 134, revenue: '$4,020', trend: 'up' },
    { name: 'Organic Cotton Polo', sales: 98, revenue: '$2,450', trend: 'up' }
  ];

  // Dummy data for customer insights
  const customerInsights = [
    { country: 'United States', orders: 45, revenue: '$15,230', percentage: 35 },
    { country: 'United Kingdom', orders: 32, revenue: '$10,890', percentage: 25 },
    { country: 'Canada', orders: 28, revenue: '$8,760', percentage: 20 },
    { country: 'Australia', orders: 21, revenue: '$6,540', percentage: 15 },
    { country: 'Germany', orders: 18, revenue: '$4,180', percentage: 5 }
  ];

  // Dummy data for recent activity
  const recentActivity = [
    {
      type: 'order',
      message: 'New order received from John Smith',
      time: '2 hours ago'
    },
    {
      type: 'review',
      message: 'Received 5-star review for Cotton T-Shirt',
      time: '4 hours ago'
    },
    {
      type: 'view',
      message: 'Product viewed 25 times today',
      time: '6 hours ago'
    },
    {
      type: 'inquiry',
      message: 'New inquiry about bulk order',
      time: '8 hours ago'
    },
    {
      type: 'order',
      message: 'Order shipped to Sarah Johnson',
      time: '1 day ago'
    }
  ];

  // Note: Analytics data would be fetched from database when analytics system is implemented
  React.useEffect(() => {
    // Placeholder for future analytics data fetching
  }, []);

  const metrics = [
    {
      title: 'Total Revenue',
      value: `$${analyticsData.revenue.toLocaleString()}`,
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Orders',
      value: analyticsData.orders.toString(),
      change: '+8.2%',
      trend: 'up',
      icon: Package,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Customers',
      value: analyticsData.customers.toString(),
      change: '+15.3%',
      trend: 'up',
      icon: Users,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Avg. Rating',
      value: analyticsData.rating.toString(),
      change: '+0.2',
      trend: 'up',
      icon: Star,
      color: 'text-yellow-600 bg-yellow-100'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1>
              <p className="text-gray-600">Track your performance and insights</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.trend === 'up' ? (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    <span>{metric.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${metric.color}`}>
                  <metric.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Chart */}
          <div className="lg:col-span-2 space-y-8">
            {/* Revenue Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Performance Overview</h2>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setSelectedMetric('revenue')}
                    className={`text-sm px-3 py-1 rounded ${selectedMetric === 'revenue' ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    Revenue
                  </button>
                  <button 
                    onClick={() => setSelectedMetric('orders')}
                    className={`text-sm px-3 py-1 rounded ${selectedMetric === 'orders' ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    Orders
                  </button>
                  <button 
                    onClick={() => setSelectedMetric('customers')}
                    className={`text-sm px-3 py-1 rounded ${selectedMetric === 'customers' ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    Customers
                  </button>
                </div>
              </div>
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Chart visualization would go here</p>
                  <p className="text-sm text-gray-500">Integration with charting library needed</p>
                </div>
              </div>
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Top Performing Products</h2>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">View All</button>
              </div>
              {topProducts.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Product Data</h3>
                  <p className="text-gray-600">Add products and receive orders to see analytics.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">{product.name}</h3>
                        <p className="text-xs text-gray-600">{product.orders} orders</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{product.revenue}</p>
                        <div className={`flex items-center text-xs ${
                          product.growth.startsWith('+') ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {product.growth.startsWith('+') ? (
                            <TrendingUp className="h-3 w-3 mr-1" />
                          ) : (
                            <ArrowDown className="h-3 w-3 mr-1" />
                          )}
                          <span>{product.growth}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Customer Insights */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Customer Insights</h2>
              {customerInsights.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Customer Data</h3>
                  <p className="text-gray-600">Customer insights will appear here once you receive orders.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customerInsights.map((insight, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{insight.country}</div>
                        <div className="text-xs text-gray-500">{insight.orders} orders</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{insight.revenue}</div>
                        <div className="text-xs text-gray-500">{insight.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h2>
              {recentActivity.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Activity Yet</h3>
                  <p className="text-gray-600">Your recent activity will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'order' ? 'bg-blue-100' :
                        activity.type === 'review' ? 'bg-yellow-100' :
                        activity.type === 'view' ? 'bg-green-100' :
                        'bg-purple-100'
                      }`}>
                        {activity.type === 'order' && <Package className="h-4 w-4 text-blue-600" />}
                        {activity.type === 'review' && <Star className="h-4 w-4 text-yellow-600" />}
                        {activity.type === 'view' && <Eye className="h-4 w-4 text-green-600" />}
                        {activity.type === 'inquiry' && <Users className="h-4 w-4 text-purple-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Conversion Rate</span>
                  <span className="font-semibold text-gray-900">0%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Avg. Order Value</span>
                  <span className="font-semibold text-gray-900">$0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Return Rate</span>
                  <span className="font-semibold text-gray-900">0%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Customer Satisfaction</span>
                  <span className="font-semibold text-gray-900">N/A</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerAnalytics;